import { tMessage, translateMessage } from "../i18n/runtime";
import { t } from "../i18n/runtime";
import { useGameStore } from "../stores/gameStore";
import { formatMoney, formatMoneyShort } from "./primitives";
import { pathOf, wealthGeometry } from "./wealthHistory";

/**
 * The broker's net worth across the round, over the ledger it is the sum of.
 *
 * The four figures below it — cash, receivables, weapons, ore — say what the
 * player is holding. None of them says whether the player is winning, because
 * winning here is those four added up and compared with where they started, and
 * the panel was asking the player to do that in their head every few seconds
 * while a war ran.
 *
 * It sits above the row rather than beside it because it is the row's total: the
 * eye reads the line, then the parts it is made of.
 *
 * Two things it has to make visible, and they are different:
 *
 *   - the drift. Trading moves this by small amounts continuously, so the line
 *     is sampled every three seconds and its right-hand end keeps moving between
 *     samples. A chart that only redrew on some event would read as a still.
 *   - the default. When a nation is eliminated, what it owed stops being worth
 *     anything and the line drops in one step. That looks exactly like a drop
 *     from spending, so it is marked in red where it happens and counted in the
 *     caption — otherwise the most expensive thing that can happen to a broker
 *     is indistinguishable from having bought something.
 */

const W = 300;
const H = 44;

export function WealthChart() {
  const history = useGameStore((s) => s.wealth);
  const geometry = wealthGeometry(history, W, H);

  // Before the first patch there is no line, and a box drawn around nothing
  // reads as a broken chart rather than an empty one.
  if (!geometry || history.length < 2) return null;

  const { line, area, latest, opening, defaults } = geometry;
  const change = latest - opening;
  const tone = change > 0 ? "up" : change < 0 ? "down" : "flat";
  const pct = opening === 0 ? 0 : (change / Math.abs(opening)) * 100;
  const head = line[line.length - 1];

  return (
    <div className={`wealth wealth--${tone}`}>
      <div className="wealth__head">
        <span className="wealth__label label">
          {t("WealthChart.total_assets")}
        </span>
        <span className="wealth__value">{formatMoney(latest)}</span>
        <span className="wealth__delta">
          {change >= 0 ? "+" : "−"}
          {formatMoneyShort(Math.abs(change))}
          <span className="wealth__pct">
            {" "}
            {change >= 0 ? "+" : "−"}
            {Math.abs(pct).toFixed(1)}%
          </span>
        </span>
      </div>

      <svg
        className="wealth__plot"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={tMessage(
          "WealthChart.total_assets_since_opening",
          (_locale) => ({
            p0: formatMoney(latest),
            p1:
              change >= 0
                ? translateMessage(_locale, "WealthChart.increase", [])
                : translateMessage(_locale, "WealthChart.decrease", []),
            p2: formatMoneyShort(Math.abs(change)),
          }),
        )}
      >
        <path className="wealth__area" d={`${pathOf(area)} Z`} />
        <path className="wealth__line" d={pathOf(line)} />

        {/* Where a counterparty went under and took its debt with it. A rule the
            full height of the box, so it reads as an event on the round rather
            than as a bump on the line. */}
        {defaults.map((point, i) => (
          <g className="wealth__default" key={`${point.x}-${i}`}>
            <line x1={point.x} y1={0} x2={point.x} y2={H} />
            <circle cx={point.x} cy={point.y} r={2.6} />
          </g>
        ))}

        <circle className="wealth__head-dot" cx={head.x} cy={head.y} r={2.6} />
      </svg>

      {defaults.length > 0 && (
        <div className="wealth__note">
          {t("WealthChart.defaults_count", { p0: defaults.length })}
        </div>
      )}
    </div>
  );
}
